import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {CHEM_SEARCH_OPERATOR_TO_DISPLAY_NAME, SIMILARITY_SCORE_CUTOFFS} from 'chemistry/ChemistryConstants';
import {getSimilarityOptionLabel, ToggleHighlightParams} from 'chemistry/ChemSearchUtils';
import {
  CHEMICAL_STRUCTURE_SEARCH_OPERATORS,
  ChemicalStructureSearchOperator,
  Operator,
} from 'search/SearchConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {Title} from 'ui-library/Headers';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import ToggleGroup from 'ui-library/ToggleGroup';

interface ChemSearchModalOptionsProps {
  highlightChecked: boolean;
  setHighlightChecked: (highlightChecked: boolean) => void;
  chemSearchOperator: ChemicalStructureSearchOperator;
  setChemSearchOperator: (operator: ChemicalStructureSearchOperator) => void;
  minSimilarity: number;
  setMinSimilarity: (minSimilarity: number) => void;
  toggleHighlightParams: ToggleHighlightParams;
  className?: string;
}

export default function ChemSearchModalOptions({
  highlightChecked,
  setHighlightChecked,
  chemSearchOperator,
  setChemSearchOperator,
  minSimilarity,
  setMinSimilarity,
  toggleHighlightParams,
  className,
}: ChemSearchModalOptionsProps): JSX.Element {
  const searchTypeToggle = (
    <div className={css(styles.searchTypeToggle)}>
      <ToggleGroup
        isFullWidth={true}
        size={ButtonSize.SMALL}
        options={CHEMICAL_STRUCTURE_SEARCH_OPERATORS.map((operator) => ({
          id: operator,
          buttonProps: {text: CHEM_SEARCH_OPERATOR_TO_DISPLAY_NAME[operator]},
        }))}
        selection={chemSearchOperator}
        onChangeSelection={(val) => {
          setChemSearchOperator(val);
        }}
      />
    </div>
  );

  const toggleHighlightOption = toggleHighlightParams.showToggleHighlightOption && (
    <label>
      <CheckboxInput
        checked={highlightChecked}
        id="toggleHighlightOption"
        onChange={(e) => {
          setHighlightChecked(e.target.checked);
        }}
        label="Highlight substructure in search results."
      />
    </label>
  );

  const similarityDropdownOptions = SIMILARITY_SCORE_CUTOFFS.map((cutoff) => (
    <Option
      dataTestAttrs={{component: 'ChemSearchModalOptions', element: 'option', key: `${cutoff}`}}
      value={cutoff}
      onSelect={setMinSimilarity}
      key={cutoff}
    >
      {getSimilarityOptionLabel(cutoff)}
    </Option>
  ));
  const similaritySearchOptions = (
    <FormGroup>
      <FieldLabel label="Similarity score" />
      <SelectDropdown
        className={css(styles.similaritySearchOptions)}
        displayName={getSimilarityOptionLabel(minSimilarity)}
        menu={
          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.SMALL} value={minSimilarity}>
            {similarityDropdownOptions}
          </Menu>
        }
      />
      <p className={css(styles.explanationText)}>
        Similarity searches are performed using{' '}
        <a
          className={css(styles.explanationTextLink)}
          href="https://jcheminf.biomedcentral.com/articles/10.1186/s13321-015-0069-3"
          target="_blank"
          rel="noopener noreferrer"
        >
          Tanimoto similarity
        </a>
        .
      </p>
    </FormGroup>
  );

  return (
    <div className={className}>
      <Title>Specify search options</Title>
      {searchTypeToggle}
      <div className={css(styles.searchOptions)}>
        {chemSearchOperator === Operator.SUBSTRUCTURE ? toggleHighlightOption : similaritySearchOptions}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  searchOptions: {
    paddingTop: 12,
  },
  searchTypeToggle: {
    paddingTop: 12,
  },
  explanationText: {
    paddingTop: 12,
    color: GRAY_DARKER,
  },
  explanationTextLink: {
    color: GRAY_DARKER,
    textDecorationLine: 'underline',
  },
  similaritySearchOptions: {
    width: '50%',
  },
});
